/**
 * <TutorPrescriptionStudentCard> — un élève dans « Travail entre les lives » (organism).
 *
 * La carte du legacy (`StudyMissionPrescriptionScreen.js`, `StudentRow`), à parité. Ses deux blocs
 * portent deux gestes indépendants : tenir ce que l'élève a DÉJÀ à faire (la croix retire, après
 * confirmation, tout de suite) et lui DONNER ce que Sam propose (« Donner à faire » n'envoie que
 * « À ajouter », jamais « Déjà à faire »). Les deux blocs ont la même ligne (`<ActivityRow>`, sans
 * chrono, la marque « Quizz » à droite) et la même croix à droite : un seul vocabulaire.
 *
 * Quatre formes, selon l'élève :
 *  - la présence n'a pas répondu : son nom, et la forme de ce qui vient (`<DelayedSkeleton>`) ;
 *  - absent du live : la ligne d'un élève absent, la même que dans le bilan (`<TutorAbsentStudentLine>`) ;
 *  - conclu (`outcome`) : une ligne repliée — « Jules M. », « 4 exercices à faire, dont 2 nouveaux » —
 *    et « Annuler », sans liseré : la forme repliée suffit, et l'attention passe à qui reste.
 * L'élève est toujours la ligne de personne du design system (`<PersonRow>`) : visage, nom, ce qu'on
 * dit de lui, et ce qu'elle porte au bout.
 *  - sinon la carte entière.
 *
 * « Recommencer » est collé au bloc qu'il remplace (sa place dit sur quoi il agit) et garde sa place
 * pendant la recherche, avec ses mots d'attente (« Je recommence… ») : le remplacer par un
 * indicateur faisait lire « le bouton ne fait plus rien ». Même chose pour « Ajouter un exercice ».
 * Sans rien à donner, le principal devient « Enregistrer » : la carte se replie, rien ne part.
 *
 * Contrôlée (§ « Contrôlé ») : la présence, la proposition, le geste en vol et la conclusion arrivent
 * en props (`UITutorPrescriptionStudent`) ; la carte ne garde que sa largeur mesurée — sous 440 px
 * les deux boutons du pied s'empilent, le principal en pleine largeur.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { ActivityRow } from "../../molecules/ActivityRow"
import { Card } from "../../molecules/Card"
import { CardListRow } from "../../molecules/CardList"
import { PersonRow } from "../../molecules/PersonRow"
import { TutorAbsentStudentLine } from "../../molecules/TutorAbsentStudentLine"
import {
    type UITutorPrescriptionActivity,
    type UITutorPrescriptionStudent,
} from "../../../ports/primary/ui/types/tutorPrescription"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"

/** Sous cette largeur de carte, « Ajouter un exercice » et « Donner à faire » ne tiennent plus côte à côte. */
const SIDE_BY_SIDE_FROM = 440

export type TutorPrescriptionStudentCardProps = {
    student: UITutorPrescriptionStudent
    /** Une régénération est en vol pour un AUTRE élève : le formulaire n'existe qu'en un exemplaire. */
    regenerateLocked?: boolean
    /** Un retrait est en vol, pour cet élève ou un autre : les croix de « Déjà à faire » attendent. */
    removalLocked?: boolean
    /** Toucher une ligne de « Déjà à faire » ouvre l'activité. */
    onOpenActivity?: (activity: UITutorPrescriptionActivity) => void
    /** La croix d'une ligne de « Déjà à faire » — la page demande confirmation. */
    onAskRemoveActivity?: (activity: UITutorPrescriptionActivity) => void
    /** Toucher une proposition l'ouvre en aperçu. */
    onOpenProposal?: (index: number) => void
    /** La croix d'une proposition la retire de « À ajouter », sans rien demander. */
    onDropProposal?: (index: number) => void
    onRegenerate?: () => void
    onAddActivity?: () => void
    /** « Donner à faire » quand « À ajouter » a des lignes, « Enregistrer » sinon. */
    onConfirm?: () => void
    /** « Annuler » sur la carte repliée. */
    onUndo?: () => void
    onRetryDraft?: () => void
    testID?: string
}

/** « 4 exercices à faire, dont 2 nouveaux » — ce que l'élève a réellement à faire, pas seulement ce qui vient de partir. */
function outcomeSummary(student: UITutorPrescriptionStudent): string {
    const newCount = student.outcome?.kind === "prescribed" ? student.outcome.newCount : 0
    const kept = student.existingActivities.length
    const total = kept + newCount
    if (total === 0) return "rien à faire"
    const head = `${countLabel(total, "exercice")} à faire`
    return newCount > 0 && kept > 0 ? `${head}, dont ${countLabel(newCount, "nouveau", "nouveaux")}` : head
}

/** La marque « Quizz » d'une activité précédée d'un quiz de prérequis, la même dans les deux blocs. */
function QuizMark(props: { activity: UITutorPrescriptionActivity }) {
    return props.activity.withQuiz === true ? (
        <Badge text="Quizz" variant="primary" tinted iconName="question-circle" />
    ) : null
}

export function TutorPrescriptionStudentCard(props: TutorPrescriptionStudentCardProps) {
    const {
        student,
        regenerateLocked = false,
        removalLocked = false,
        onOpenActivity,
        onAskRemoveActivity,
        onOpenProposal,
        onDropProposal,
        onRegenerate,
        onAddActivity,
        onConfirm,
        onUndo,
        onRetryDraft,
        testID,
    } = props
    const { name, className, avatarUrl, presence, existingActivities, draft, outcome, busy, removingActivityId } =
        student
    const { onLayout, atLeast: sideBySide } = useWidthAtLeast(SIDE_BY_SIDE_FROM)

    // ── La présence n'a pas encore répondu : le nom est connu, il s'affiche tout de suite ──
    if (presence === "pending") {
        return (
            <Card p="0" testID={testID}>
                <PersonRow
                    name={name}
                    avatarUrl={avatarUrl}
                    trailing={
                        <DelayedSkeleton>
                            <Skeleton shape="chip" width={96} />
                        </DelayedSkeleton>
                    }
                />
            </Card>
        )
    }

    // ── Absent : rien à lui donner ──
    if (presence === "absent") return <TutorAbsentStudentLine name={name} avatarUrl={avatarUrl} testID={testID} />

    // ── Conclu : la carte se replie, l'attention passe à qui reste ──
    if (outcome != null) {
        return (
            <Card p="0" testID={testID}>
                <PersonRow
                    name={name}
                    avatarUrl={avatarUrl}
                    caption={outcomeSummary(student)}
                    trailing={
                        // « Annuler », pas « Retirer » : « Déjà à faire » a déjà sa croix qui retire.
                        <Button
                            label="Annuler"
                            variant="ghost"
                            size="sm"
                            onPress={onUndo}
                            loading={busy === "unprescribing"}
                            disabled={busy === "unprescribing"}
                        />
                    }
                />
            </Card>
        )
    }

    const regenerating = busy === "regenerating"
    const proposals = draft?.state === "ready" ? draft.proposals : []

    return (
        <Card p="0" testID={testID}>
            <View onLayout={onLayout}>
                <PersonRow name={name} avatarUrl={avatarUrl} caption={className} />

                {/* ── Déjà à faire ── */}
                {existingActivities.length > 0 && (
                    <>
                        <CardListRow divided py="2">
                            {/* « À faire » est le mot de l'élève (son onglet) ; « TODO » était le nôtre. */}
                            <SectionLabel>{`Déjà à faire (${existingActivities.length})`}</SectionLabel>
                        </CardListRow>
                        {existingActivities.map((activity) => (
                            <View key={`existing-${activity.id}`} style={styles.divided}>
                                <Row align="center" style={styles.line}>
                                    <View style={styles.grow}>
                                        <ActivityRow
                                            activity={activity}
                                            onPress={
                                                onOpenActivity != null ? () => onOpenActivity(activity) : undefined
                                            }
                                            trailing={<QuizMark activity={activity} />}
                                        />
                                    </View>
                                    {/* Elle retire ce que l'élève a déjà : la page demande avant. Seule la
                                        ligne visée tourne, le verrou fige les autres sans les faire clignoter. */}
                                    <IconButton
                                        iconName="times"
                                        appearance="transparent"
                                        iconColor="text-muted"
                                        size={36}
                                        iconSize={16}
                                        loading={removingActivityId === activity.id}
                                        disabled={removalLocked}
                                        onPress={() => onAskRemoveActivity?.(activity)}
                                        accessibilityLabel={`Retirer « ${activity.title} »`}
                                    />
                                </Row>
                            </View>
                        ))}
                    </>
                )}

                {/* ── Ce que Sam propose ── */}
                {draft == null || draft.state === "loading" ? (
                    <CardListRow divided>
                        <WaitingText size="text-sm" color="text-muted" align="start">
                            Je prépare la séquence…
                        </WaitingText>
                    </CardListRow>
                ) : draft.state === "failed" ? (
                    <CardListRow divided>
                        <Stack gap="2" align="start">
                            {/* Le nom en milieu de phrase : un nom comme « Karim D. » finit déjà par un point. */}
                            <Text size="text-md" color="warning-strong">
                                {`La séquence de ${name} n'a pas pu être préparée.`}
                            </Text>
                            <Button
                                label="Réessayer"
                                variant="ghost"
                                size="sm"
                                iconName="redo"
                                onPress={onRetryDraft}
                            />
                        </Stack>
                    </CardListRow>
                ) : (
                    <>
                        <CardListRow divided py="2">
                            <Row align="center" justify="between" gap="2">
                                <SectionLabel>{`À ajouter (${proposals.length})`}</SectionLabel>
                                <Button
                                    label={regenerating ? "Je recommence…" : "Recommencer"}
                                    variant="ghost"
                                    size="sm"
                                    iconName={regenerating ? undefined : "redo"}
                                    onPress={onRegenerate}
                                    disabled={regenerating || regenerateLocked}
                                />
                            </Row>
                        </CardListRow>

                        {proposals.length === 0 ? (
                            <CardListRow divided>
                                {draft.emptiedByTutor === true ? (
                                    <Text size="text-md" color="text-muted">
                                        Tu as retiré toutes les propositions. Recommence, ajoute un exercice, ou
                                        enregistre tel quel.
                                    </Text>
                                ) : (
                                    <Stack gap="1">
                                        <Text size="text-md" color="warning-strong">
                                            Je n&apos;ai pas pu générer d&apos;exercice automatique (matière ou chapitre
                                            manquants).
                                        </Text>
                                        <Text size="text-sm" color="text-muted">
                                            Tu peux lui donner un exercice à la main depuis le bouton + de sa liste « À
                                            faire ».
                                        </Text>
                                    </Stack>
                                )}
                            </CardListRow>
                        ) : (
                            proposals.map((activity, index) => (
                                <View key={`proposal-${activity.id}`} style={styles.divided}>
                                    <Row align="center" style={styles.line}>
                                        <View style={styles.grow}>
                                            <ActivityRow
                                                activity={activity}
                                                onPress={
                                                    onOpenProposal != null ? () => onOpenProposal(index) : undefined
                                                }
                                                trailing={<QuizMark activity={activity} />}
                                            />
                                        </View>
                                        <IconButton
                                            iconName="times"
                                            appearance="transparent"
                                            iconColor="text-muted"
                                            size={36}
                                            iconSize={16}
                                            onPress={() => onDropProposal?.(index)}
                                            accessibilityLabel={`Ne pas ajouter « ${activity.title} »`}
                                        />
                                    </Row>
                                </View>
                            ))
                        )}

                        {/* Le pied : le secondaire à gauche, le principal à droite. « Ajouter un exercice »
                            reste offert même après avoir tout retiré, sinon « ajoute-en un » n'aurait aucun bouton. */}
                        <CardListRow divided>
                            <View style={sideBySide ? styles.footerRow : styles.footerStack}>
                                <Button
                                    label={busy === "adding" ? "Je génère un exercice…" : "Ajouter un exercice"}
                                    variant="ghost"
                                    size="sm"
                                    iconName={busy === "adding" ? undefined : "plus"}
                                    onPress={onAddActivity}
                                    disabled={busy === "adding" || regenerating}
                                />
                                {/* Les mots du masteur à l'élève (« je t'ai donné deux exercices à faire »). */}
                                <Button
                                    label={proposals.length > 0 ? "Donner à faire" : "Enregistrer"}
                                    iconName="check"
                                    onPress={onConfirm}
                                    loading={busy === "prescribing"}
                                    disabled={busy === "prescribing" || regenerating}
                                    fullWidth={!sideBySide}
                                />
                            </View>
                        </CardListRow>
                    </>
                )}
            </View>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    grow: {
        flex: 1,
        flexShrink: 1,
        minWidth: 0,
    },
    line: {
        paddingRight: theme.spacing["2"],
    },
    divided: {
        borderTopWidth: 1,
        borderTopColor: theme.colors["border-card"],
    },
    footerRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing["2"],
    },
    footerStack: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: theme.spacing["2"],
    },
}))
